import { type Content, type Editor, EditorContent } from "@tiptap/react";
import * as React from "react";
import "./styles/index.css";

import { LinkBubbleMenu } from "./components/bubble-menu/link-bubble-menu";
import { MeasuredContainer } from "./components/measured-container";
import ElementMenuBar from "./components/section/element-menu-bar";
import HeadingMenuBar from "./components/section/heading-menu-bar";
import ListMenuBar from "./components/section/list-menu-bar";
import TextColorMenuBar from "./components/section/text-color-menu-bar";
import TextFormatMenuBar from "./components/section/text-formatter-menu-bar";
import {
  type UseMinimalTiptapEditorProps,
  useMinimalTiptapEditor,
} from "./hooks/use-minimal-tiptap";

import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export interface MinimalTiptapProps
  extends Omit<UseMinimalTiptapEditorProps, "onUpdate"> {
  value?: Content;
  onChange?: (value: Content) => void;
  className?: string;
  editorContentClassName?: string;
  customChildren?: React.ReactNode;
}

const Toolbar = ({ editor }: { editor: Editor }): JSX.Element => (
  <div className='shrink-0 overflow-x-auto border-b border-border p-2 sticky top-0 bg-white z-20'>
    <div className='flex w-max items-center gap-px'>
      <HeadingMenuBar editor={editor} activeLevels={[1, 2, 3, 4, 5, 6]} />

      <Separator orientation='vertical' className='mx-2 h-7' />

      <TextFormatMenuBar
        editor={editor}
        activeActions={[
          "bold",
          "italic",
          "strikethrough",
          "code",
          "clearFormatting",
        ]}
        mainActionCount={2}
      />

      <Separator orientation='vertical' className='mx-2 h-7' />

      <TextColorMenuBar editor={editor} />

      <Separator orientation='vertical' className='mx-2 h-7' />

      <ListMenuBar
        editor={editor}
        activeActions={["bulletList", "orderedList"]}
        mainActionCount={2}
        variant='outline'
      />

      <Separator orientation='vertical' className='mx-2 h-7' />

      <ElementMenuBar
        editor={editor}
        activeActions={["codeBlock", "blockquote", "horizontalRule"]}
        mainActionCount={0}
      />
    </div>
  </div>
);

export const MinimalTiptapEditor = React.forwardRef<
  HTMLDivElement,
  MinimalTiptapProps
>(
  (
    {
      value,
      onChange,
      className,
      editorContentClassName,
      customChildren,
      ...props
    },
    ref,
  ) => {
    const editor = useMinimalTiptapEditor({
      value,
      onUpdate: onChange,
      ...props,
    });

    if (!editor) {
      return null;
    }

    return (
      <MeasuredContainer
        as='section'
        name='editor'
        ref={ref}
        className={cn(
          "flex h-auto w-full flex-col rounded-md border border-input shadow-sm focus-within:border-primary ",
          className,
        )}
      >
        <Toolbar editor={editor} />
        <EditorContent
          editor={editor}
          className={cn("minimal-tiptap-editor p-5 ", editorContentClassName)}
        />
        <LinkBubbleMenu editor={editor} />
        {customChildren}
      </MeasuredContainer>
    );
  },
);

MinimalTiptapEditor.displayName = "MinimalTiptapEditor";

export default MinimalTiptapEditor;
